import { SCROLL_PAGE_SIZE } from "../../../redux/shared/paged-list.ts"
import type { LeadsFilters } from "./leads.types.ts"

export const leadsSliceName = "leads"

// The slice is persisted, so a drill-through has to reset the siblings rather
// than merge onto whatever the user last left behind.
export const LEADS_DEFAULT_FILTERS: LeadsFilters = {
  q: null,
  page: 1,
  page_size: SCROLL_PAGE_SIZE,
  // Not user-facing and deliberately absent from LEADS_FILTER_KEYS, so
  // "Clear All" cannot drop the table back to showing inactive leads.
  view: "active",
  status: null,
  sales_manager: null,
  customer_name: null,
  created_after: null,
  created_before: null,
  operational_states: null,
  follow_up_date_after: null,
  follow_up_date_before: null,
  follow_up_owner: null,
  source: null,
  state: null,
  project_type: null,
  preferred_contact_method: null,
  show_converted: null,
  is_closed_lost: null,
  archived_at_after: null,
  archived_at_before: null
}

// Everything "Clear All" and the active-filter count consider a filter, as
// opposed to paging and the search box which the toolbar owns separately.
export const LEADS_FILTER_KEYS = [
  "status",
  "operational_states",
  "follow_up_date_after",
  "follow_up_date_before",
  "follow_up_owner",
  "sales_manager",
  "source",
  "customer_name",
  "created_after",
  "created_before",
  "state",
  "project_type",
  "preferred_contact_method",
  "show_converted",
  // No control writes these — a KPI drill does. They are listed all the same,
  // so Clear All can reach a narrowing the user cannot see and the active
  // count admits the table is filtered.
  "is_closed_lost",
  "archived_at_after",
  "archived_at_before"
] as const satisfies readonly (keyof LeadsFilters)[]

/**
 * Filters that describe *when* or *who* rather than *what*, and are therefore
 * wrong when restored into a later session.
 *
 * `sales_manager` is set by the "My Active Leads" chip and names a specific
 * user, so a persisted value survives a sign-out and silently filters the table
 * for whoever comes next. The date pair is set by "New Today" from today's
 * date, so tomorrow it quietly means "since yesterday" while the chip that set
 * it no longer reads as selected.
 *
 * Both leave the table empty or short with nothing on screen explaining why,
 * which is what makes a working page look broken.
 */
export const LEADS_SESSION_SCOPED_FILTER_KEYS = [
  "sales_manager",
  "created_after",
  "created_before",
  // The Closed-Lost drill bounds the month it was clicked in, so a restored
  // pair means a month the user is no longer looking at — and the tile that
  // set it is not on screen to explain the short table.
  "archived_at_after",
  "archived_at_before"
] as const satisfies readonly (keyof LeadsFilters)[]

// The subset behind "More Filters", which drives that button's count badge.
export const LEADS_ADVANCED_FILTER_KEYS = [
  "customer_name",
  "created_after",
  "created_before",
  "state",
  "project_type",
  "preferred_contact_method",
  "show_converted"
] as const satisfies readonly (keyof LeadsFilters)[]

// A range picker writes both ends at once, so the pair is one filter to the
// user and must not be counted twice.
const FILTER_GROUP_BY_KEY: Partial<Record<keyof LeadsFilters, string>> = {
  created_after: "created",
  created_before: "created",
  follow_up_date_after: "follow_up_date",
  follow_up_date_before: "follow_up_date",
  archived_at_after: "archived_at",
  archived_at_before: "archived_at"
}

export const isFilterActive = (value: unknown): boolean => {
  if (value == null || value === "" || value === false) return false
  return Array.isArray(value) ? value.length > 0 : true
}

/**
 * Nulls a filter that has been emptied. `[] || null` is `[]`, and serializeData
 * only joins a non-empty array — so a cleared multi-select would otherwise
 * reach the request as an empty param rather than being dropped.
 */
export const normalizeFilterValue = (value: unknown): unknown =>
  isFilterActive(value) ? value : null

export const countActiveFilters = (
  filters: LeadsFilters,
  keys: readonly (keyof LeadsFilters)[]
): number =>
  keys.reduce((groups, key) => {
    if (isFilterActive(filters?.[key])) {
      groups.add(FILTER_GROUP_BY_KEY[key] ?? key)
    }
    return groups
  }, new Set<string>()).size

export const clearedFilters = (
  keys: readonly (keyof LeadsFilters)[]
): Partial<LeadsFilters> =>
  Object.fromEntries(keys.map((key) => [key, null])) as Partial<LeadsFilters>

/**
 * The Active-table helpers below (`clearedActiveFilters`/`matchesActiveFilters`
 * /`narrowsAnything`) and their Inactive-table counterparts
 * (`clearedInactiveFilters`/`matchesInactiveFilters`/`narrowsInactiveFilters`)
 * share one implementation, parameterised by which keys count as "a filter"
 * and what "cleared" restores `view` to — the two tables differ only in that,
 * not in the comparison logic itself.
 */
const buildCleared =
  (keys: readonly (keyof LeadsFilters)[], restoreView: LeadsFilters["view"]) =>
  (): Partial<LeadsFilters> => ({
    ...clearedFilters(keys),
    view: restoreView
  })

/**
 * Whether `filters` is exactly the narrowing `target` describes — the test both
 * the preset chips and the KPI tiles read their selected state from.
 *
 * Comparing against the live filters, rather than tracking whichever control
 * was clicked last, is what lets the two rows stay honest about each other: a
 * chip that replaces a tile's narrowing un-selects the tile without either
 * having to know the other exists.
 */
const buildMatches =
  (keys: readonly (keyof LeadsFilters)[]) =>
  (
    target: Partial<LeadsFilters>,
    filters: LeadsFilters | null | undefined
  ): boolean =>
    keys.every((key) => {
      const wanted = target[key] ?? null
      const current = filters?.[key] ?? null

      if (Array.isArray(wanted) || Array.isArray(current)) {
        const a = (Array.isArray(wanted) ? wanted : []).map(String).sort()
        const b = (Array.isArray(current) ? current : []).map(String).sort()
        return a.length === b.length && a.every((value, i) => value === b[i])
      }

      return wanted === current
    })

/** Whether a narrowing constrains anything at all, as opposed to matching an
 * unfiltered page and so reading as selected on load. */
const buildNarrows =
  (keys: readonly (keyof LeadsFilters)[]) =>
  (target: Partial<LeadsFilters>): boolean =>
    keys.some((key) => {
      const wanted = target[key] ?? null
      return Array.isArray(wanted) ? wanted.length > 0 : wanted !== null
    })

/**
 * Everything "Clear All" and the preset chips reset the Active table to.
 *
 * `view` is restored rather than cleared. It is not one of the user's filters —
 * nulling it would drop the table back to showing inactive leads — but the
 * Closed-Lost drill widens it to `all`, and a widening left behind outlives the
 * tile that explains it. Restoring the page's own view is what both callers
 * mean by "clear".
 */
export const clearedActiveFilters = buildCleared(
  LEADS_FILTER_KEYS,
  LEADS_DEFAULT_FILTERS.view
)
export const matchesActiveFilters = buildMatches(LEADS_FILTER_KEYS)
export const narrowsAnything = buildNarrows(LEADS_FILTER_KEYS)

// The Inactive table's default filters and filter-key set — `view` defaults
// to "inactive" instead of "active", and `reason` is the only real filter
// this page has today (no `show_converted`/`is_closed_lost`: `reason=won`
// already covers that ground for Inactive).
export const INACTIVE_LEADS_DEFAULT_FILTERS: LeadsFilters = {
  q: null,
  page: 1,
  page_size: SCROLL_PAGE_SIZE,
  view: "inactive",
  reason: null
}

export const INACTIVE_LEADS_FILTER_KEYS = [
  "reason"
] as const satisfies readonly (keyof LeadsFilters)[]

export const clearedInactiveFilters = buildCleared(
  INACTIVE_LEADS_FILTER_KEYS,
  INACTIVE_LEADS_DEFAULT_FILTERS.view
)
export const matchesInactiveFilters = buildMatches(INACTIVE_LEADS_FILTER_KEYS)
export const narrowsInactiveFilters = buildNarrows(INACTIVE_LEADS_FILTER_KEYS)
